import Cookies from 'js-cookie';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { getCityzensProfile } from 'services/account/get/get-cityzens-profile.api';
import type { GetCityzensProfileResponse } from 'services/account/get/types/get-cityzens-profile.type';

import { PUBLIC_SUPPORTS_LOGGED_IN_USERS } from 'tools/environments';

let refreshInFlight: Promise<void> | null = null;
const supportsLoggedInUsers = PUBLIC_SUPPORTS_LOGGED_IN_USERS === 'true';

export type UserProps = GetCityzensProfileResponse & {
	isLoggedIn?: boolean;
};

type UserState = {
	user: UserProps | null;
	lastUserFetchedAt: number | null;
};

type UserActions = {
	setUser: (user: UserProps) => void;
	clearUser: () => void;
	refreshUser: () => Promise<void>;
};

type UseUserStoreProps = UserState & UserActions;

const emptyUserState: UserState = {
	user: null,
	lastUserFetchedAt: null,
};

export const useUserStore = create<UseUserStoreProps>()(
	persist<UseUserStoreProps>(
		(set, get) => ({
			user: null,
			lastUserFetchedAt: null,
			setUser: (user) => {
				if (!supportsLoggedInUsers) {
					set(() => emptyUserState);
					return;
				}

				const tempUser = { ...user, isLoggedIn: true };

				if (tempUser.avatar?.url) {
					tempUser.avatar.url += `?${new Date().getTime()}`;
				}

				set(() => ({ user: tempUser, lastUserFetchedAt: Date.now() }));
			},
			clearUser: () => set(() => emptyUserState),
			refreshUser: async () => {
				// Skip profile refresh during build time (SSG/SSR)
				if (typeof window === 'undefined') {
					return;
				}

				if (!supportsLoggedInUsers) {
					get().clearUser();
					return;
				}

				if (refreshInFlight) return refreshInFlight;

				refreshInFlight = getCityzensProfile()
					.then((data) => {
						if (!data) {
							get().clearUser();
							return;
						}
						get().setUser(data);
					})
					.catch((error) => {
						console.error('Profile refresh failed:', error);
					})
					.finally(() => {
						refreshInFlight = null;
					});

				return refreshInFlight;
			},
		}),
		{
			name: 'cfg_user_store',
			storage: createJSONStorage(() => ({
				getItem: (key) => {
					if (!supportsLoggedInUsers) {
						Cookies.remove(key);
						return null;
					}

					return Cookies.get(key) ?? null;
				},
				setItem: (key, value) => {
					if (!supportsLoggedInUsers) {
						Cookies.remove(key);
						return;
					}

					Cookies.set(key, value, {
						expires: 3,
						secure: process.env.NODE_ENV === 'production',
						sameSite: 'Lax',
					});
				},
				removeItem: (key) => Cookies.remove(key),
			})),
			version: 1.2,
		}
	)
);
